import { useContext, useEffect, useState } from 'react';
import { setIsLoggingIn, setUser } from '@/features/user-session';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, IconButton, List, ListItem, ListItemText, Popover } from '@mui/material';
import { Link, useNavigate } from '@tanstack/react-router';
import NotificationsQuickView from 'components/Notifications/NotificationsQuickView';
import { http } from 'config';
import { resetStore, useAppDispatch, useAppSelector } from 'features';
import { addNotification } from 'features/Notifications';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Nprogress from 'nprogress';
import { UserSessionContext } from 'providers/UserSessionProvider';
import { userSignOut } from 'services/accounts';
import { toast } from 'sonner';
import { type WebsocketNotification } from 'types/client';
import { type Notification, type User } from 'types/swagger';
import IntlMessages from 'util/IntlMessages';

const getUserInitials = (user: User) =>
  user.first_name && user.last_name
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
    : user.first_name
      ? user.first_name.charAt(0)
      : user?.email?.charAt(0);

const SettingsNotificationsButton = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const { user, setShouldRedirect, notificationsWebsocket } = useContext(UserSessionContext);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const unseenNotifications = useAppSelector((state) => state.notifications.unseenCount);
  useDocumentTitle(
    unseenNotifications ? `(${unseenNotifications}) Enerlink: CMS` : 'Enerlink: CMS'
  );
  useEffect(() => {
    if (notificationsWebsocket) {
      const newWebsocket: WebSocket = notificationsWebsocket;
      newWebsocket.onmessage = (event) => {
        const json = JSON.parse(event?.data ? event.data : undefined);
        if (json && json.type === 'authorization' && json.message === false) {
          newWebsocket.close();
        }
        if (json?.message?.data && json.message.message) {
          const receivedNotification: WebsocketNotification = json.message.data;
          const newNotification: Notification = {
            id: receivedNotification.notification,
            method: 'IN_APP',
            data: JSON.stringify(receivedNotification),
            title: '',
            action_code: receivedNotification.action_code,
            message: json.message.message,
            created: new Date().toISOString(),
            updated: new Date().toISOString(),
            seen: false,
          };
          dispatch(addNotification(newNotification));
        }
      };
    }

    return () => {
      if (notificationsWebsocket) {
        const newWebsocket = notificationsWebsocket;
        newWebsocket.onmessage = () => {};
      }
    };
  }, [dispatch, notificationsWebsocket, notifications]);

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuOpen(!isMenuOpen);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuOpen(false);
  };

  const clearSession = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('currentInstance');
    dispatch(setUser(null));
    setShouldRedirect(true);
    dispatch(resetStore());
  };
  // ! Reminder to update this after migration is done
  const handleSignOut = () => {
    Nprogress.start();
    dispatch(setIsLoggingIn(true));
    userSignOut(localStorage.getItem('refresh') || '')
      .catch(() => {})
      .finally(() => {
        http.defaults.headers.common.Authorization = undefined;
        clearSession();
        Nprogress.done();
        dispatch(setIsLoggingIn(false));
        void navigate({ to: '/', replace: true });
        toast.success('Sesión finalizada');
      });
  };
  return (
    user && (
      <div className="notifications-preferences-container mr-1">
        <NotificationsQuickView />
        <Avatar
          sx={{
            color: 'whitesmoke',
            background: 'rgba(255,255,255,0.5)',
          }}
          alt={getUserInitials(user)?.toUpperCase()}
        >
          {getUserInitials(user)?.toUpperCase()}
        </Avatar>
        <IconButton onClick={handleClickMenu}>
          <ExpandMoreIcon />
        </IconButton>
        <Popover
          open={isMenuOpen}
          anchorEl={menuAnchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPopover-paper': {
              width: 150,
              marginTop: 2,
            },
          }}
        >
          <List className="settings-menu-popover">
            <Link className="no-text-decoration" to="/preferences">
              <ListItem>
                <ListItemText sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  <IntlMessages id="pages.settings" />
                </ListItemText>
              </ListItem>
            </Link>
            <button
              type="button"
              className="bg-transparent border-0 p-0 w-100"
              onClick={handleSignOut}
            >
              <ListItem>
                <ListItemText sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  <IntlMessages id="sidenav.popup.logout" />
                </ListItemText>
              </ListItem>
            </button>
          </List>
        </Popover>
      </div>
    )
  );
};

export default SettingsNotificationsButton;
